import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import type { CountryCode } from 'libphonenumber-js';
import type { PhoneNumberPurpose } from '@court-sync/shared';
import { Field, FieldError } from './Field';
import { PhoneInput } from './PhoneInput';
import { PhoneNumberLabel } from './PhoneNumberLabel';
import type { DefaultPhoneCountrySource } from '@/hooks/useDefaultPhoneCountry';

export type PhoneFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  id?: string;
  required?: boolean;
  purpose?: PhoneNumberPurpose;
  defaultCountry?: CountryCode;
  defaultCountrySource?: DefaultPhoneCountrySource;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
};

export function PhoneField<T extends FieldValues>({
  control,
  name,
  label,
  id,
  required,
  purpose = 'general',
  defaultCountry,
  defaultCountrySource,
  disabled,
  placeholder,
  className,
}: PhoneFieldProps<T>) {
  const fieldId = id ?? String(name);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={!!fieldState.error} className={className}>
          {label ? (
            <PhoneNumberLabel htmlFor={fieldId} required={required} purpose={purpose}>
              {label}
            </PhoneNumberLabel>
          ) : null}
          <PhoneInput
            id={fieldId}
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            defaultCountry={defaultCountry}
            defaultCountrySource={defaultCountrySource}
            disabled={disabled}
            error={!!fieldState.error}
            placeholder={placeholder}
            aria-invalid={!!fieldState.error}
          />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  );
}
